import { Controller } from "stimulus";
import MitreTable from '../utils/mitre-table';

const FILTER_SELECTED_CSS_CLASS = 'mp-page-filter-body-area-power-filters-filter__selected';

const UiWidgetType = {
  SELECT: 'select',
  MULTISELECT: 'multiselect'
};

const isParentNode = (el, parentEl, numSteps) => {
  let stepsLeft = numSteps;
  let currentParentEl = el.parentNode;
  while (stepsLeft > 0) {
    if (currentParentEl === parentEl) return true;
    currentParentEl = currentParentEl.parentNode;
    stepsLeft -= 1;
  }
  return false;
}

class CollectionController extends Controller {
  static targets = [
    'filterGroup',
    'filterButton'
  ];

  initialize() {
    const tableKey = this.element.closest('[data-table-key-value]').getAttribute('data-table-key-value');
    this._mitreTable = MitreTable.getByTableKey(tableKey);
  }

  connect() {
    // NB! Mind that these filter values are actually read from DOM by the table_controller.
    const { collectionKey, filters } = this._mitreTable.getParams();

    this._refreshCollectionChangingFilterButtons(collectionKey);
    this._refreshRegularFilterButtons(filters);
    this._refreshFilterGroups(filters);

    this._mitreTable.emit('update_collection'); // listeners: tabs_controller.js

    this._mitreTable.on('update_collection_filter', () => {  // emitters: securities_filter_controller.js
      this._mitreTable.emit('update_filter_counter'); // listeners: filter_controller.js
    });
  }

  update(e) {
    this._mitreTable.emit('interaction', { type: MitreTable.InteractionType.FILTER_CHANGE });
    if (MitreTable.areInteractionsLocked()) return;

    // NB! Because of the aspects of disabling/enabling filter button groups,
    // we cannot rely on the state stored in this._mitreTable at all times.
    // A disabled button can be both selected and unselected, so we'll be
    // reading state from DOM at every click and will not include
    // selected+disabled buttons in the this._mitreTable's state.

    const filterButtonEl = e.currentTarget;

    const {
      filterKey,
      filterValue,
      filterType,
      collectionKey,
    } = filterButtonEl.dataset;

    if (filterKey) {
      const filtersBeforeDOMUpdate = this._readFiltersFromDOM();

      const filterGroupEl = this.filterGroupTargets.find((filterGroupEl) => {
        return this._getFilterGroupButtons(filterGroupEl).includes(filterButtonEl);
      });

      const {
        uiWidgetType,
        uiWidgetAllowNull
      } = filterGroupEl.dataset;

      // NB1! Multiselect: If uiWidgetAllowNull is true, we allow any combination to be selected.
      // Otherwise, we don't allow to unselect the choice if it's the only choice so far.
      // ---
      // NB2! Multiselect: Only filters (not collection keys) can be a multiselect widget.
      // That's logical and common sense, but we're making it explicit in the code.
      // ---
      if (uiWidgetType === UiWidgetType.MULTISELECT && uiWidgetAllowNull !== 'true') {
        const filterGroupButtonEls = this._getFilterGroupButtons(filterGroupEl);
        const filterGroupButtonKeys = filterGroupButtonEls.map((el) => el.dataset.filterKey);

        const selectedSoFar = filterGroupButtonKeys.map((filterKey) => filtersBeforeDOMUpdate[filterKey]).filter((one) => Boolean(one));
        if (selectedSoFar.length === 1
            && selectedSoFar[0].filterKey === filterKey
              && selectedSoFar[0].value === filterValue) {
          return;
        }
      }

      if (filtersBeforeDOMUpdate[filterKey]
            && filtersBeforeDOMUpdate[filterKey].value === filterValue) {
        delete filterButtonEl.dataset.filterSelected;
        delete filtersBeforeDOMUpdate[filterKey];
      } else {
        filterButtonEl.dataset.filterSelected = true;
        filtersBeforeDOMUpdate[filterKey] = {
          filterKey,
          selected: true,
          value: filterValue,
          type: filterType
        }
      }

      this._refreshRegularFilterButtons();
      this._refreshFilterGroups(filtersBeforeDOMUpdate);

      const filtersAfterDOMUpdate = this._readFiltersFromDOM();
      this._mitreTable.setParam('filters', filtersAfterDOMUpdate);
    }

    else if (collectionKey) {
      this._refreshCollectionChangingFilterButtons(collectionKey);
      this._mitreTable.setParam('collectionKey', collectionKey);
    }

    this._mitreTable.emit('table-body-update', { pagination: 1 });

    if (collectionKey) {
      this._mitreTable.emit('update_collection');              // listeners: tabs_controller.js
      this._mitreTable.emit('update_collection_keys_filter');  // listeners: securities_filter_controller.js
      this._mitreTable.emit('update_filter_counter');          // listeners: filter_controller.js
    }
  }

  _readFiltersFromDOM() {
    const filters = {};

    this.filterGroupTargets.forEach((filterGroupEl) => {
      if (filterGroupEl.dataset.disabled) return;
      this._getFilterGroupButtons(filterGroupEl).forEach((filterButtonEl) => {
        const {
          filterKey,
          filterType,
          filterValue,
          filterSelected,
        } = filterButtonEl.dataset;

        if (filterSelected) {
          filters[filterKey] = {
            filterKey,
            selected: true,
            value: filterValue,
            type: filterType
          };
        }
      });
    });

    return filters;
  }

  _refreshCollectionChangingFilterButtons(collectionKey) {
    this.filterButtonTargets.forEach((el) => {
      if (el.dataset['collectionKey']) {
        if (el.dataset['collectionKey'] === collectionKey) {
          el.classList.add(FILTER_SELECTED_CSS_CLASS);
        } else {
          el.classList.remove(FILTER_SELECTED_CSS_CLASS);
        }
      }
    });
  }

  _refreshRegularFilterButtons() {
    this.filterButtonTargets.forEach((el) => {
      if (el.dataset.filterKey) {
        if (el.dataset.filterSelected) {
          el.classList.add(FILTER_SELECTED_CSS_CLASS);
        } else {
          el.classList.remove(FILTER_SELECTED_CSS_CLASS);
        }
      }
    });
  }

  _refreshFilterGroups(filters) {
    this.filterGroupTargets.forEach((el) => {
      let requirements = this.constructor.getFilterGroupRequirements(el);
      if (requirements.length === 0) return;
      requirements.forEach(([filterKey, filterValue]) => {
        if (filters[filterKey]
              && filters[filterKey].selected
              && filters[filterKey].value === filterValue) {
          this._enableFilterGroup(el);
        } else {
          this._disableFilterGroup(el);
        }
      });
    });
  }

  _getFilterGroupButtons(filterGroupEl) {
    return this.filterButtonTargets.filter(
      (filterButtonEl) => isParentNode(filterButtonEl, filterGroupEl, 5));
  }

  _disableFilterGroup(filterGroupEl) {
    filterGroupEl.dataset.disabled = true;
    filterGroupEl.classList.add('t-opacity-25');
    this._getFilterGroupButtons(filterGroupEl).forEach((filterGroupButtonEl) => {
      filterGroupButtonEl.classList.add('t-cursor-not-allowed');
      filterGroupButtonEl.setAttribute('disabled', 'disabled');
    });
  }

  _enableFilterGroup(filterGroupEl) {
    delete filterGroupEl.dataset.disabled;
    filterGroupEl.classList.remove('t-opacity-25');
    this._getFilterGroupButtons(filterGroupEl).forEach((filterGroupButtonEl) => {
      filterGroupButtonEl.classList.remove('t-cursor-not-allowed');
      filterGroupButtonEl.removeAttribute('disabled');
    });
  }

  static getFilterGroupRequirements(filterGroupEl) {
    let requirements = [];
    const requirementsJSON = filterGroupEl.dataset.uiWidgetRequirements;
    if (requirementsJSON) {
      try {
        requirements = JSON.parse(requirementsJSON);
      } catch (err) {
        // Swallow for now.
        // console.log(err);
        // TODO(michael.g): integrate with some tool to keep track of these errors.
      }
    }
    return requirements;
  }
}

export default CollectionController;
